import { ComponentType, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch } from 'react-redux';
import { updateUser } from '../../store/userSlice';
import { updateRoles } from '../../store/rolesSlice';
import { updateUserResponse } from '../../store/userResponseSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useNavigate } from 'react-router-dom';
import { IUser } from '../../types/reduxStore';
import { IUserDetail } from '../../types/reduxStore';
import { GetUser, GetUserPageAccess } from '../../services/user-service';
import { IUserPageAccessResponse } from '../../types/manage';
import { indexedDBService } from '../../services/indexdb-service';

interface WithProtectedRouteProps {
    redirectTo?: string;
}

export const withProtectedRoute = <P extends object>(
    WrappedComponent: ComponentType<P>,
    options?: WithProtectedRouteProps
) => {

    return (props: P) => {
        const { isLoading, isAuthenticated, user, getAccessTokenSilently, handleRedirectCallback } = useAuth0();
        const dispatch = useDispatch();
        const navigate = useNavigate();

        const userDetails = useSelector((state: RootState) => state.userDetail);

        const initApplication = async (token) => {
            if (user && user.nickname && user.name && user.picture && user.updated_at && user.email) {
                const userState: IUser = {
                    nickname: user.nickname,
                    name: user.name,
                    picture: user.picture,
                    updated_at: user.updated_at,
                    email: user.email,
                };
                dispatch(updateUser(userState));
                await indexedDBService.setItem('User', 'updateUser', user);
            }

            localStorage.setItem('auth0token', token);
            if (user && user.email && !localStorage.getItem('email')) {
                localStorage.setItem('email', user.email);
            }

            await GetUser()
                .then(async (userResp: IUserDetail) => {
                    localStorage.setItem("user", btoa(JSON.stringify(userResp)));
                    dispatch(updateUserResponse(userResp));
                    await indexedDBService.setItem('User', 'userResp', userResp);
                })
                .catch(error => {
                    console.error('Error GetUser:', error);
                });

            await GetUserPageAccess()
                .then(async (userPageAccessResponse: IUserPageAccessResponse) => {
                    dispatch(updateRoles({ userPageAccess: userPageAccessResponse.userPageAccess }));
                    await indexedDBService.setItem('User', 'userRoles', { userPageAccess: userPageAccessResponse.userPageAccess });
                })
                .catch(error => {
                    console.error('Error GetUserPageAccess:', error);

                });
        }

        const fetchFromIndexDb = async () => {
            const userState = await indexedDBService.getItem('User', 'updateUser');
            const userResp = await indexedDBService.getItem('User', 'userResp');
            const userRoles = await indexedDBService.getItem('User', 'userRoles');
            dispatch(updateUser(userState));
            dispatch(updateUserResponse(userResp));
            dispatch(updateRoles(userRoles));
        }

        const getExpirationDate = () => {
            const jwtToken = localStorage.getItem('auth0token');
            // if null treated as a string here. therefore, testing === "null" too
            if (!jwtToken || jwtToken === "null") {
                return null;
            }
            const jwt = JSON.parse(atob(jwtToken.split('.')[1]));
            return (jwt && jwt.exp && jwt.exp * 1000) || null;
        };

        const validateToken = () => {
            const exp = getExpirationDate();
            if (!exp) {
                return false;
            }
            return Date.now() < exp;
        };

        useEffect(() => {
            const fetchAndInit = async () => {
                if (isLoading) {
                    return;
                }
                if (localStorage.getItem('auth0token')) {
                    if (validateToken()) {
                        if (userDetails !== null) {
                            initApplication(localStorage.getItem('auth0token'));
                        }
                    }
                    else {
                        if (isAuthenticated) {
                            const token = await getAccessTokenSilently();
                            localStorage.setItem('auth0token', token);
                            if (validateToken()) {
                                if (userDetails !== null) {
                                    initApplication(token);
                                }
                            }
                        }
                        else {
                            navigate("/logout")
                        }
                    }
                }
                else {
                    try {
                        if (isAuthenticated) {
                            const token = await getAccessTokenSilently();
                            localStorage.setItem('auth0token', token);
                            if (validateToken()) {
                                if (userDetails !== null) {
                                    initApplication(token);
                                }
                            }
                        }
                        else {
                            navigate("/logout")
                        }

                    } catch (error) {
                        navigate("/logout")
                    }
                }
            };
            fetchAndInit();
        }, [isAuthenticated, isLoading]);
        return <WrappedComponent {...(props as P)} />;
    };
};


